/**
 * Product-page cover-preview uploader - client.
 *
 * Wires the (previously display-only) uploader in the kb-start-personalising
 * section to the factory backend. Flow:
 *
 *   upload (photo) → details (language / name / age) → generating → redirect
 *                                                          ↓
 *                                                        error → details
 *
 * Backend contract (set via the section's `backend_url` setting):
 *   POST {backend}/upload-url
 *     body: { filename, content_type, size, theme_id, child_name, age, language, turnstile_token }
 *     → { presigned_put_url, photo_key, generation_id }
 *     (the backend names the photo after the child, so send the details too)
 *   PUT  {presigned_put_url}            (raw file → object storage)
 *   POST {backend}/preview
 *     body: { theme_id, photo_key, generation_id, child_name, age, language, turnstile_token }
 *     → { preview_url, generation_id, status: 'ready' | 'queued' | 'failed' }
 *     If queued: poll GET {backend}/preview-status/<generation_id>
 *
 * The backend resolves the cover image + AI prompt server-side from the
 * product's metafields (keyed by theme_id), so this client never sends or
 * exposes them. On success we stash the result in sessionStorage and redirect
 * to /pages/create-your-book to choose a cover and continue to checkout.
 *
 * If `data-backend-url` is empty the uploader stays display-only - we bail
 * before binding anything so the section never errors.
 */

(function () {
  'use strict';

  var root = document.querySelector('[data-kb-upload]');
  if (!root) return;

  // The backend is only needed for the final generation step. The wizard
  // itself (photo → details form) is fully client-side, so we wire it up
  // regardless; if the backend is missing we surface a message only when the
  // user actually tries to generate.
  var backendUrl = (root.getAttribute('data-backend-url') || '').replace(/\/+$/, '');

  var themeId = root.getAttribute('data-theme-id') || '';
  var productTitle = root.getAttribute('data-product-title') || '';
  var turnstileKey = root.getAttribute('data-turnstile-key') || '';

  // The landing page's book picker (kb-landing-picker.liquid) swaps the book
  // under this uploader without a page load. Everything downstream - the
  // /preview call, the pixel event, the hand-off payload - reads these vars,
  // so updating them here is the whole switch.
  function applyBook(d) {
    if (!d || !d.handle) return;
    themeId = d.handle;
    productTitle = d.title || '';
    root.setAttribute('data-theme-id', themeId);
    root.setAttribute('data-product-title', productTitle);
    if (d.price) root.setAttribute('data-cover-price', String(d.price));
    var available = d.available !== false;
    root.setAttribute('data-product-available', available ? 'true' : 'false');
    if (els.input) {
      if (available) els.input.removeAttribute('disabled'); else els.input.setAttribute('disabled', '');
    }
    if (els.trigger) {
      if (available) els.trigger.removeAttribute('aria-disabled'); else els.trigger.setAttribute('aria-disabled', 'true');
    }
    if (available) clearUploadMsg(); else showUploadMsg(msg.unavailable);
    updateChosen(d);
  }

  // Landing page: the "Their story" card beside the uploader mirrors the
  // picker's selection (cover, short title, price).
  var chosen = (root.closest('.kb-start-personalising__card') || document).querySelector('[data-kb-chosen]');
  function updateChosen(d) {
    if (!chosen) return;
    var cover = chosen.querySelector('[data-kb-chosen-cover]');
    var title = chosen.querySelector('[data-kb-chosen-title]');
    var price = chosen.querySelector('[data-kb-chosen-price]');
    if (title && d.title) title.textContent = d.title.split(':')[0].trim();
    // "From" price = cheapest variant, matching the picker cards; d.price is
    // the default variant (used for the pixel's value) and can be higher.
    if (price && (d.fromPrice || d.price)) {
      var cents = parseInt(d.fromPrice || d.price, 10);
      var currency = root.getAttribute('data-currency') || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      var formatted = '';
      try {
        formatted = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency }).format(cents / 100);
      } catch (e) { formatted = (cents / 100).toFixed(2) + ' ' + currency; }
      price.textContent = (chosen.getAttribute('data-price-template') || 'From __PRICE__').replace('__PRICE__', formatted);
    }
    if (cover && d.cover && cover.getAttribute('src') !== d.cover) {
      chosen.classList.add('is-swapping');
      var next = new Image();
      next.onload = next.onerror = function () {
        cover.removeAttribute('srcset');
        cover.src = d.cover;
        chosen.classList.remove('is-swapping');
      };
      next.src = d.cover;
    }
  }

  document.addEventListener('kb:book-change', function (e) { applyBook(e && e.detail); });

  // Signed-in customer proof. Rendered by Liquid only when the shopper is
  // logged in AND the shared secret is configured; blank otherwise, in which
  // case we simply send nothing and get the anonymous allowance.
  var customerId = root.getAttribute('data-customer-id') || '';
  var tokenDate = root.getAttribute('data-token-date') || '';
  var customerToken = root.getAttribute('data-customer-token') || '';
  var loginUrl = root.getAttribute('data-login-url') || '';

  var msg = {
    uploading: root.getAttribute('data-msg-status-uploading') || 'Sending the photo…',
    generating: root.getAttribute('data-msg-status-generating') || 'Painting their cover…',
    previewAlt: root.getAttribute('data-msg-preview-alt') || 'Personalised book cover preview',
    tooLarge: root.getAttribute('data-msg-error-too-large') || 'That photo is too large.',
    wrongType: root.getAttribute('data-msg-error-wrong-type') || 'Please choose a JPG or PNG image.',
    upload: root.getAttribute('data-msg-error-upload') || 'We had trouble uploading that photo.',
    preview: root.getAttribute('data-msg-error-preview') || "We're having trouble with this photo.",
    timeout: root.getAttribute('data-msg-error-timeout') || 'This is taking longer than usual. Please try again.',
    config: root.getAttribute('data-msg-error-config') || 'Previews aren’t available right now. Please try again later.',
    generic: root.getAttribute('data-msg-error-generic') || 'Something went wrong. Please try again.',
    challenge: root.getAttribute('data-msg-error-challenge') || 'Please complete the verification first.',
    name: root.getAttribute('data-msg-error-name') || 'Please add their first name.',
    age: root.getAttribute('data-msg-error-age') || 'Please add their age (up to 12).',
    unavailable: root.getAttribute('data-msg-product-unavailable') || 'This book is currently unavailable. Please try again soon.',
    limitAnon: root.getAttribute('data-msg-limit-anon') || 'You’ve used your free previews — sign in with Google or email (takes 20 seconds) to unlock more free previews.',
    limitAnonCta: root.getAttribute('data-msg-limit-anon-cta') || 'Sign in to keep previewing',
    limitCustomer: root.getAttribute('data-msg-limit-customer') || 'You’ve reached today’s preview limit — previews reset tomorrow, or continue with your favourite preview.',
    signinCta: root.getAttribute('data-msg-signin-cta') || 'Sign in to see your next preview',
    signinHint: root.getAttribute('data-msg-signin-hint') || 'Save your previews and unlock more — sign in with Google or email.',
    loaderTitle: root.getAttribute('data-msg-loader-title') || 'Painting {{ name }}’s cover.',
    loaderStages: (root.getAttribute('data-msg-loader-stages') || '').split('|').filter(Boolean)
  };

  var els = {
    input: root.querySelector('[data-kb-upload-input]'),
    trigger: root.querySelector('[data-kb-upload-trigger]'),
    steps: {
      upload: root.querySelector('[data-kb-step="upload"]'),
      details: root.querySelector('[data-kb-step="details"]'),
      status: root.querySelector('[data-kb-step="status"]')
    },
    thumb: root.querySelector('[data-kb-upload-thumb]'),
    name: root.querySelector('[data-kb-field="name"]'),
    age: root.querySelector('[data-kb-field="age"]'),
    language: root.querySelector('[data-kb-field="language"]'),
    generate: root.querySelector('[data-kb-upload-generate]'),
    fieldError: root.querySelector('[data-kb-field-error]'),
    limitGate: root.querySelector('[data-kb-limit-gate]'),
    limitText: root.querySelector('[data-kb-limit-text]'),
    signinBtn: root.querySelector('[data-kb-signin-btn]'),
    remaining: root.querySelector('[data-kb-remaining]'),
    statusText: root.querySelector('[data-kb-upload-status-text]'),
    uploadMsg: root.querySelector('[data-kb-upload-msg]'),
    turnstile: root.querySelector('[data-kb-turnstile]'),
    // Tips strip lives outside [data-kb-upload], in the shared panel.
    tips: (root.closest('.kb-start-personalising__panel') || document).querySelector('[data-kb-tips]'),
    // Full-page loader lives outside the card, at the section root.
    pageLoader: (root.closest('section') || document).querySelector('[data-kb-page-loader]'),
    loaderTitle: (root.closest('section') || document).querySelector('[data-kb-loader-title]'),
    loaderStatus: (root.closest('section') || document).querySelector('[data-kb-loader-status]'),
    loaderRail: (root.closest('section') || document).querySelector('[data-kb-loader-rail]'),
    loaderMotes: (root.closest('section') || document).querySelector('[data-kb-loader-motes]'),
    loaderPhotos: (root.closest('section') || document).querySelectorAll('[data-kb-loader-photo]')
  };

  if (!els.input || !els.steps.upload || !els.steps.details) return;

  // The picker's own script may have announced its initial selection before
  // this file loaded (section JS is bundled into the deferred theme script,
  // which runs first). Catch up from the checked radio so both agree.
  (function syncFromPicker() {
    var picked = document.querySelector('input[name="kb-landing-pick"]:checked');
    if (!picked) return;
    applyBook({
      handle: picked.value,
      title: picked.getAttribute('data-title') || '',
      price: picked.getAttribute('data-price') || '',
      available: picked.getAttribute('data-available') === 'true',
      cover: picked.getAttribute('data-cover') || '',
      fromPrice: picked.getAttribute('data-from-price') || ''
    });
  })();

  if (root.getAttribute('data-product-available') !== 'true') {
    els.input.setAttribute('disabled', '');
    if (els.trigger) els.trigger.setAttribute('aria-disabled', 'true');
    showUploadMsg(msg.unavailable);
    return;
  }

  var MAX_MB = parseInt(els.input.getAttribute('data-kb-upload-max-mb'), 10) || 10;
  // Cap inline-to-function payloads well under serverless body limits, and keep
  // fal's per-megapixel cost down (the child photo is the largest input). A face
  // only needs ~1024px; we downscale anything larger client-side before sending.
  var DOWNSCALE_MAX_PX = 1024;

  var state = {
    file: null,
    objectUrl: null,
    photoKey: null,
    generationId: null,
    previewUrl: null,
    consentAt: null,
    turnstileToken: '',
    turnstileWidgetId: null
  };

  /* -------------------------------------------------------------- */
  /*  Turnstile (optional bot challenge)                            */
  /* -------------------------------------------------------------- */

  function renderTurnstile() {
    if (!turnstileKey || !els.turnstile) return;
    if (!window.turnstile) {
      // Script not loaded yet - retry briefly; it's injected on demand below.
      return;
    }
    if (state.turnstileWidgetId !== null) return;
    state.turnstileWidgetId = window.turnstile.render(els.turnstile, {
      sitekey: turnstileKey,
      // Managed mode, rendered only if Cloudflare actually needs to challenge.
      // 'interaction-only' suppresses the "Success!" confirmation box for the
      // common no-challenge case; the token still arrives via `callback`, and
      // validateDetails()/freshTurnstileToken() still gate submission on it.
      appearance: 'interaction-only',
      callback: function (token) { state.turnstileToken = token; },
      'expired-callback': function () { state.turnstileToken = ''; },
      'error-callback': function () { state.turnstileToken = ''; }
    });
  }

  function ensureTurnstileScript() {
    if (!turnstileKey) return;
    if (window.turnstile) { renderTurnstile(); return; }
    if (document.querySelector('script[data-kb-turnstile-script]')) return;
    var s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true;
    s.defer = true;
    s.setAttribute('data-kb-turnstile-script', '');
    s.addEventListener('load', renderTurnstile);
    document.head.appendChild(s);
  }

  function resetTurnstile() {
    if (window.turnstile && state.turnstileWidgetId !== null) {
      try { window.turnstile.reset(state.turnstileWidgetId); } catch (e) { /* noop */ }
    }
    state.turnstileToken = '';
  }

  // Turnstile tokens are single-use: once /upload-url has verified one, the
  // /preview call needs a fresh token. Reset the widget and wait briefly for
  // the new token (managed/invisible mode re-solves without interaction).
  function freshTurnstileToken(ms) {
    if (!turnstileKey) return Promise.resolve('');
    resetTurnstile();
    return new Promise(function (resolve) {
      var start = Date.now();
      (function check() {
        if (state.turnstileToken) return resolve(state.turnstileToken);
        if (Date.now() - start > ms) return resolve('');
        setTimeout(check, 250);
      })();
    });
  }

  /* -------------------------------------------------------------- */
  /*  Networking helper - fetch with a hard timeout                 */
  /* -------------------------------------------------------------- */

  // Wraps fetch with an AbortController timeout so a hung request can never
  // leave the loader spinning forever - it rejects and the flow shows an error.
  function fetchWithTimeout(url, opts, ms) {
    opts = opts || {};
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, ms);
    opts.signal = controller.signal;
    return fetch(url, opts).finally(function () { clearTimeout(timer); });
  }

  /* -------------------------------------------------------------- */
  /*  Step transitions                                              */
  /* -------------------------------------------------------------- */

  function setStep(next, opts) {
    opts = opts || {};
    Object.keys(els.steps).forEach(function (key) {
      var el = els.steps[key];
      if (!el) return;
      if (key === next) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
    // Tips only make sense while choosing a photo; hide them past the upload step.
    if (els.tips) {
      if (next === 'upload') els.tips.removeAttribute('hidden');
      else els.tips.setAttribute('hidden', '');
    }
    if (next === 'status' && opts.statusText && els.statusText) {
      els.statusText.textContent = opts.statusText;
    }
  }

  /* -------------------------------------------------------------- */
  /*  Full-page loader - "the studio"                               */
  /* -------------------------------------------------------------- */

  // Blocks the whole page (not just the uploader) for the 30-60s generation,
  // and narrates it: the child's own photo on a book cover, a brush sweeping
  // it from grey reference into finished colour art. Turning dead wait time
  // into the product's own story is the point - a spinner would waste it.

  var loaderState = { timer: null, stage: 0, lastFocus: null };

  var REDUCED_MOTION = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  // Drift the studio dust. Skipped entirely under reduced motion, and kept
  // modest so the loader never becomes the page's heaviest paint.
  function seedMotes() {
    if (!els.loaderMotes || els.loaderMotes.childElementCount) return;
    if (REDUCED_MOTION && REDUCED_MOTION.matches) return;
    var frag = document.createDocumentFragment();
    for (var i = 0; i < 14; i++) {
      var m = document.createElement('span');
      var dur = 9 + Math.random() * 10;
      m.className = 'kb-loader__mote';
      m.style.left = (Math.random() * 100) + '%';
      m.style.setProperty('--dx', (Math.random() * 80 - 40) + 'px');
      m.style.animationDuration = dur + 's';
      // Negative delay starts each mote mid-flight, so the field looks
      // established the instant the loader appears rather than filling in.
      m.style.animationDelay = (-Math.random() * dur) + 's';
      m.style.opacity = 0.3 + Math.random() * 0.5;
      frag.appendChild(m);
    }
    els.loaderMotes.appendChild(frag);
  }

  // Swap the status line by replacing the node, so its enter animation
  // re-runs on every stage (restarting a CSS animation needs a fresh node).
  function setLoaderStage(text) {
    if (!els.loaderStatus) return;
    var next = els.loaderStatus.cloneNode(false);
    next.textContent = text;
    els.loaderStatus.replaceWith(next);
    els.loaderStatus = next;
  }

  function setRail(fraction) {
    if (!els.loaderRail) return;
    els.loaderRail.style.transform = 'scaleX(' + Math.max(0, Math.min(1, fraction)) + ')';
  }

  // The stage list is a narration of the real pipeline, not a fake progress
  // bar: we advance on a timer because the backend reports no intermediate
  // progress, but the rail never reaches 100% until the preview truly lands.
  function startLoaderNarration() {
    var stages = msg.loaderStages;
    if (!stages.length) return;
    loaderState.stage = 0;
    setLoaderStage(stages[0]);
    setRail(0.06);
    // ~7s per stage paces the list across a typical 45s generation without
    // running out of stages on a slow one (it holds on the last).
    loaderState.timer = window.setInterval(function () {
      loaderState.stage++;
      if (loaderState.stage >= stages.length) {
        window.clearInterval(loaderState.timer);
        loaderState.timer = null;
        return;
      }
      setLoaderStage(stages[loaderState.stage]);
      setRail(Math.min(0.92, (loaderState.stage + 1) / stages.length));
    }, 7000);
  }

  function stopLoaderNarration() {
    if (loaderState.timer) {
      window.clearInterval(loaderState.timer);
      loaderState.timer = null;
    }
  }

  function showPageLoader(childName) {
    if (!els.pageLoader) return;

    // Use the child's actual photo so the animation is theirs, not stock.
    if (state.objectUrl && els.loaderPhotos) {
      Array.prototype.forEach.call(els.loaderPhotos, function (img) {
        img.src = state.objectUrl;
      });
    }
    if (els.loaderTitle) {
      els.loaderTitle.textContent = msg.loaderTitle.replace('{{ name }}', childName || '');
    }

    seedMotes();
    els.pageLoader.removeAttribute('hidden');
    document.documentElement.style.overflow = 'hidden';

    // Keep focus inside the loader while it owns the screen, and hide the
    // page behind it from assistive tech.
    loaderState.lastFocus = document.activeElement;
    els.pageLoader.setAttribute('tabindex', '-1');
    try { els.pageLoader.focus({ preventScroll: true }); } catch (e) { /* older browsers */ }
    document.addEventListener('keydown', trapLoaderFocus, true);

    startLoaderNarration();
  }

  function hidePageLoader() {
    if (!els.pageLoader) return;
    stopLoaderNarration();
    els.pageLoader.setAttribute('hidden', '');
    document.documentElement.style.overflow = '';
    // Drop references to the object URL - a later reset/reselect revokes it,
    // and we don't want the loader holding a stale blob src.
    if (els.loaderPhotos) {
      Array.prototype.forEach.call(els.loaderPhotos, function (img) { img.removeAttribute('src'); });
    }
    setRail(0);
    document.removeEventListener('keydown', trapLoaderFocus, true);
    if (loaderState.lastFocus && loaderState.lastFocus.focus) {
      try { loaderState.lastFocus.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    }
    loaderState.lastFocus = null;
  }

  // Nothing inside the loader is focusable, so Tab has nowhere legitimate to
  // go - swallow it rather than letting focus wander onto the blocked page.
  function trapLoaderFocus(e) {
    if (e.key === 'Tab') {
      e.preventDefault();
      if (els.pageLoader && els.pageLoader.focus) els.pageLoader.focus({ preventScroll: true });
    }
  }

  // Drive the rail to full on success, so the last thing the user sees before
  // the redirect is a completed bar rather than one frozen mid-way.
  function completePageLoader() {
    stopLoaderNarration();
    setRail(1);
  }

  function showFieldError(text) {
    if (!els.fieldError) return;
    els.fieldError.textContent = text;
    els.fieldError.removeAttribute('hidden');
  }
  function clearFieldError() {
    if (!els.fieldError) return;
    els.fieldError.textContent = '';
    els.fieldError.setAttribute('hidden', '');
  }
  // Inline message under the upload button (pre-details validation errors).
  function showUploadMsg(text) {
    if (!els.uploadMsg) return;
    els.uploadMsg.textContent = text;
    els.uploadMsg.removeAttribute('hidden');
  }
  function clearUploadMsg() {
    if (!els.uploadMsg) return;
    els.uploadMsg.textContent = '';
    els.uploadMsg.setAttribute('hidden', '');
  }

  /* -------------------------------------------------------------- */
  /*  Photo selection + validation                                 */
  /* -------------------------------------------------------------- */

  els.input.addEventListener('change', onPhotoChange);

  // The backend only stores JPG/PNG, but phones hand us HEIC (iPhone), WebP
  // (WhatsApp, screenshots) and the occasional file with an empty MIME type.
  // Rather than bounce those with a format error - which most parents can't
  // act on - we decode whatever was picked and re-encode it as JPEG. iOS
  // Safari transcodes HEIC to JPEG on the way in; Android can't decode HEIC at
  // all, so that one still fails and the message suggests a screenshot.
  function isNativeType(file) {
    return /^image\/(jpe?g|png)$/.test(file.type);
  }

  function decodeImage(file) {
    if (window.createImageBitmap) {
      return window.createImageBitmap(file).catch(function () { return decodeViaImg(file); });
    }
    return decodeViaImg(file);
  }

  function decodeViaImg(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('undecodable')); };
      img.src = url;
    });
  }

  // Draws a decoded image (ImageBitmap or HTMLImageElement) to a JPEG File,
  // capped at `maxPx` on the long edge. Also strips EXIF, so orientation is
  // baked in exactly as the browser rendered it.
  function toJpeg(source, name, maxPx) {
    var w = source.width, h = source.height;
    var scale = Math.min(1, maxPx / Math.max(w, h));
    var canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    canvas.getContext('2d').drawImage(source, 0, 0, canvas.width, canvas.height);
    if (source.close) { try { source.close(); } catch (e) { /* noop */ } }
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (!blob) { reject(new Error('encode-failed')); return; }
        resolve(new File([blob], (name || 'photo').replace(/\.[a-z0-9]+$/i, '') + '.jpg', { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.9);
    });
  }

  // Native JPG/PNG under the size cap goes through untouched (maybeDownscale
  // still shrinks the big ones at generate time). Anything else is normalised
  // to a JPEG now, which doubles as the validation that it is a real image.
  function normalisePhoto(file) {
    if (isNativeType(file) && file.size <= MAX_MB * 1024 * 1024) return Promise.resolve(file);
    if (!isNativeType(file) && file.type && file.type.indexOf('image/') !== 0) {
      return Promise.reject(new Error('wrong-type'));
    }
    return decodeImage(file).then(function (decoded) {
      // Oversized native files get shrunk here instead of erroring: a 14 MB
      // iPhone photo is the normal case, not a mistake.
      return toJpeg(decoded, file.name, 2048);
    });
  }

  function onPhotoChange() {
    var file = els.input.files && els.input.files[0];
    if (!file) return;

    clearUploadMsg();
    setTriggerBusy(true);

    normalisePhoto(file).then(function (ready) {
      if (ready.size > MAX_MB * 1024 * 1024) throw new Error('too-large');
      acceptPhoto(ready);
    }).catch(function (err) {
      els.input.value = '';
      showUploadMsg(err && err.message === 'too-large' ? msg.tooLarge : msg.wrongType);
    }).then(function () {
      setTriggerBusy(false);
    });
  }

  // Swaps the upload button's label for "Reading photo…" while decoding.
  var triggerLabel = els.trigger && els.trigger.querySelector('[data-kb-upload-trigger-label]');
  var triggerIdleText = triggerLabel ? triggerLabel.textContent : '';
  function setTriggerBusy(busy) {
    if (!els.trigger) return;
    if (busy) {
      els.trigger.setAttribute('aria-busy', 'true');
      var busyText = els.trigger.getAttribute('data-busy-label');
      if (triggerLabel && busyText) triggerLabel.textContent = busyText;
    } else {
      els.trigger.removeAttribute('aria-busy');
      if (triggerLabel) triggerLabel.textContent = triggerIdleText;
    }
  }

  function acceptPhoto(file) {
    state.file = file;
    if (state.objectUrl) URL.revokeObjectURL(state.objectUrl);
    state.objectUrl = URL.createObjectURL(file);
    if (els.thumb) {
      els.thumb.src = state.objectUrl;
      els.thumb.alt = msg.previewAlt;
    }
    clearFieldError();
    setStep('details');
    ensureTurnstileScript();
    if (els.name) {
      // Defer focus so the panel is visible first.
      window.setTimeout(function () { els.name.focus(); }, 0);
    }
  }

  /* -------------------------------------------------------------- */
  /*  Generate                                                      */
  /* -------------------------------------------------------------- */

  if (els.generate) els.generate.addEventListener('click', onGenerate);

  function validateDetails() {
    var name = (els.name && els.name.value.trim()) || '';
    var age = (els.age && els.age.value || '').trim();
    var ageNum = parseInt(age, 10);
    if (!name) { showFieldError(msg.name); els.name && els.name.focus(); return false; }
    if (!Number.isFinite(ageNum) || ageNum < 0 || ageNum > 12) { showFieldError(msg.age); els.age && els.age.focus(); return false; }
    if (turnstileKey && !state.turnstileToken) { showFieldError(msg.challenge); return false; }
    clearFieldError();
    return true;
  }

  /* -------------------------------------------------------------- */
  /*  Prefill from an expired preview                               */
  /* -------------------------------------------------------------- */

  // The expired-preview page links back here with ?name=&age=&lang= so the
  // shopper only has to re-pick the photo. Values are validated on submit as
  // usual, so a hand-edited query string can't get anything past the form.
  (function prefillFromQuery() {
    var q;
    try { q = new URLSearchParams(window.location.search); } catch (e) { return; }

    var name = (q.get('name') || '').trim().slice(0, 40);
    if (name && els.name && !els.name.value) els.name.value = name;

    var age = (q.get('age') || '').trim();
    if (/^\d{1,2}$/.test(age) && els.age && !els.age.value) els.age.value = age;

    var lang = (q.get('lang') || '').trim();
    if (/^[a-z]{2}$/.test(lang) && els.language) {
      var opts = els.language.options || [];
      for (var i = 0; i < opts.length; i++) {
        if (opts[i].value === lang) { els.language.value = lang; break; }
      }
    }
  })();

  /* -------------------------------------------------------------- */
  /*  Recent previews strip                                         */
  /* -------------------------------------------------------------- */

  // Device-local list, rendered above the photo upload. Hides itself when the
  // list is empty, so a first-time visitor sees the form exactly as before.
  var recentMount = root.querySelector('[data-kb-recent]');

  function renderRecent() {
    try {
      if (window.KBRecentPreviews) window.KBRecentPreviews.render(recentMount);
    } catch (e) { /* never block the form */ }
  }

  // Pulls the account's list and merges it in. Renders the local list first so
  // the strip appears immediately, then re-renders if the sync adds anything.
  function syncRecent() {
    renderRecent();
    if (!window.KBRecentPreviews || !window.KBRecentPreviews.sync) return;
    if (!backendUrl || !customerId || !customerToken) return;
    window.KBRecentPreviews
      .sync({
        endpoint: backendUrl,
        credentials: {
          customer_id: customerId,
          token_date: tokenDate,
          customer_token: customerToken
        }
      })
      .then(renderRecent)
      .catch(function () { /* the local list is already on screen */ });
  }

  syncRecent();

  /* -------------------------------------------------------------- */
  /*  Preview analytics (Meta CustomizeProduct)                     */
  /* -------------------------------------------------------------- */

  // Fires once, the moment /preview returns a cover - i.e. for the person who
  // actually generated it. It used to fire on the ?k= preview page, which also
  // counted anyone who merely opened a shared or reloaded ?k= URL; those visits
  // now produce only the normal page view / ViewContent.
  var previewEventSent = false;

  function trackPreviewGenerated() {
    if (previewEventSent) return;
    previewEventSent = true;

    var priceCents = parseInt(root.getAttribute('data-cover-price'), 10);
    var eventPayload = {
      content_name: productTitle || themeId || '',
      content_ids: themeId ? [themeId] : [],
      content_type: 'product',
      value: Number(isFinite(priceCents) ? priceCents / 100 : 0),
      currency:
        root.getAttribute('data-currency') ||
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
        ''
    };

    // Theme-loaded pixel, when one is present on the page. CustomizeProduct is
    // the standard event (usable as a conversion goal); PreviewGenerated is a
    // custom event with an unmistakable name for Events Manager and audiences.
    try {
      if (typeof fbq === 'function') {
        fbq('track', 'CustomizeProduct', eventPayload);
        fbq('trackCustom', 'PreviewGenerated', eventPayload);
      }
    } catch (e) { /* analytics must never break the page */ }

    // Fallback for the sandboxed Facebook & Instagram channel pixel, where fbq
    // does not exist in the theme scope. A Custom Pixel subscribes to this.
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('preview_generated', eventPayload);
      }
    } catch (e) { /* analytics must never break the page */ }
  }

  /* -------------------------------------------------------------- */
  /*  Sign-in analytics (Meta CompleteRegistration)                 */
  /* -------------------------------------------------------------- */

  // Sign-in is a full-page navigation to Shopify's hosted account login, so
  // there is no in-page success callback to hook. Instead we mark that we sent
  // the shopper off (SIGNIN_PENDING_KEY, set in showLimitGate) and fire on the
  // way back, once the page renders with `customer` present. Clearing the key
  // before publishing makes this once-per-sign-in even across reloads.
  var SIGNIN_PENDING_KEY = 'kb-signin-pending';
  var signedIn = root.getAttribute('data-signed-in') === 'true';
  var signinEventSent = false;

  function trackSignInComplete() {
    if (signinEventSent) return;
    signinEventSent = true;

    var priceCents = parseInt(root.getAttribute('data-cover-price'), 10);
    var eventPayload = {
      content_name: 'preview_signin',
      status: true,
      value: Number(isFinite(priceCents) ? priceCents / 100 : 0),
      currency:
        root.getAttribute('data-currency') ||
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
        ''
    };

    // Same two-path mechanism as the CustomizeProduct event in kb-my-book.js:
    // direct fbq when a theme-loaded pixel exists, and a Shopify analytics
    // publish that a Custom Pixel subscribes to for the sandboxed channel pixel.
    try {
      if (typeof fbq === 'function') {
        fbq('track', 'CompleteRegistration', eventPayload);
      }
    } catch (e) { /* analytics must never break the page */ }

    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('preview_signin_completed', eventPayload);
      }
    } catch (e) { /* analytics must never break the page */ }
  }

  // Returned from the hosted login already authenticated → sign-in succeeded.
  (function checkSignInReturn() {
    if (!signedIn) return;
    var pending = false;
    try {
      pending = sessionStorage.getItem(SIGNIN_PENDING_KEY) === '1';
      if (pending) {
        sessionStorage.removeItem(SIGNIN_PENDING_KEY);
        sessionStorage.removeItem('kb-login-return');
      }
    } catch (e) { return; }
    if (!pending) return;
    // Signed in and back on the page: clear the gate and restore the button.
    hideLimitGate();
    trackSignInComplete();
    // Their previews now have an account to belong to - pull and push.
    try { syncRecent(); } catch (e) { /* strip is a convenience */ }
  })();

  /* -------------------------------------------------------------- */
  /*  Preview allowance: signed-in credentials, counter, limit gate  */
  /* -------------------------------------------------------------- */

  // Merges the signed customer fields into a request body when logged in.
  // Logged out, the body is unchanged and the backend applies the anon tier.
  function withCustomer(payload) {
    if (customerId && tokenDate && customerToken) {
      payload.customer_id = customerId;
      payload.token_date = tokenDate;
      payload.customer_token = customerToken;
    }
    return payload;
  }

  // "X previews left today". `remaining` is null when the backend has rate
  // limiting disabled - show nothing rather than a bogus count.
  function showRemaining(remaining) {
    if (!els.remaining) return;
    // Hide at 0 as well as for a missing count: at the limit the sign-in button
    // and its hint carry the message, so "0 previews left today." would be a
    // redundant, discouraging third line saying the same thing.
    if (typeof remaining !== 'number' || !isFinite(remaining) || remaining < 1) {
      els.remaining.hidden = true;
      els.remaining.textContent = '';
      return;
    }
    var template = remaining === 1 ? msg.remainingOne : msg.remainingOther;
    els.remaining.textContent = template.replace('{{ count }}', String(remaining));
    els.remaining.hidden = false;
  }

  // Sends the shopper back to this exact page after signing in. Shopify's new
  // customer accounts host the login flow off-site, so we pass the full URL.
  function loginUrlWithReturn() {
    var here = window.location.pathname + window.location.search + window.location.hash;
    var base = loginUrl || (window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'account/login';
    var sep = base.indexOf('?') === -1 ? '?' : '&';
    return base + sep + 'return_url=' + encodeURIComponent(here);
  }

  // Shown when the backend answers 429. `loginRequired` picks the warm
  // sign-in invitation (anonymous) or the "resets tomorrow" note (customer who
  // spent their own allowance). Either way the generate button stays disabled:
  // another tap would just burn a second 429.
  function showLimitGate(loginRequired) {
    hidePageLoader();
    setStep('details');
    clearFieldError();
    // Never "0 previews left today." - the button and hint below say it better.
    showRemaining(null);

    if (loginRequired && els.signinBtn) {
      // Swap the primary button rather than disabling it: same position, same
      // styling, but it now leads somewhere instead of being a dead control.
      try {
        sessionStorage.setItem('kb-login-return', window.location.href);
        // Marks that we sent this visitor to sign in, so when they come back
        // authenticated we can attribute the CompleteRegistration event.
        sessionStorage.setItem(SIGNIN_PENDING_KEY, '1');
      } catch (e) { /* private mode - return_url is the primary path */ }

      els.signinBtn.href = loginUrlWithReturn();
      els.signinBtn.textContent = msg.signinCta;
      els.signinBtn.hidden = false;
      if (els.generate) els.generate.hidden = true;

      if (els.limitGate && els.limitText) {
        els.limitText.textContent = msg.signinHint;
        els.limitGate.hidden = false;
      }
      return;
    }

    // Signed-in shopper who spent their own allowance: no sign-in to offer, so
    // the button stays disabled and the line explains previews reset tomorrow.
    if (els.generate) els.generate.setAttribute('disabled', '');
    if (els.limitGate && els.limitText) {
      els.limitText.textContent = msg.limitCustomer;
      els.limitGate.hidden = false;
    }
  }

  // Restores the normal state: the original button back, sign-in CTA gone.
  function hideLimitGate() {
    if (els.limitGate) els.limitGate.hidden = true;
    if (els.signinBtn) els.signinBtn.hidden = true;
    if (els.generate) {
      els.generate.hidden = false;
      els.generate.removeAttribute('disabled');
    }
  }

  // Reads { login_required } off a 429 body. Defaults to treating the visitor
  // as anonymous (the more helpful message) if the body is unreadable.
  async function limitErrorFrom(res) {
    var loginRequired = !(customerId && customerToken);
    try {
      var json = await res.json();
      if (json && typeof json.login_required === 'boolean') loginRequired = json.login_required;
    } catch (e) { /* keep the inferred default */ }
    var err = new Error('rate-limited');
    err.loginRequired = loginRequired;
    return err;
  }

  function onGenerate() {
    if (!state.file) { setStep('upload'); return; }
    if (!validateDetails()) return;
    if (!backendUrl) { showFieldError(msg.config); return; }
    run();
  }

  async function run() {
    state.consentAt = new Date().toISOString();
    var childName = els.name.value.trim();
    var age = els.age.value;
    var language = (els.language && els.language.value) || 'en';

    if (els.generate) els.generate.setAttribute('disabled', '');
    clearFieldError();
    hideLimitGate();
    setStep('status', { statusText: msg.uploading });
    showPageLoader(childName);

    try {
      var fileToSend = await maybeDownscale(state.file);

      // 1. Presigned upload URL
      var upRes = await fetchWithTimeout(backendUrl + '/upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(withCustomer({
          filename: state.file.name,
          content_type: fileToSend.type || 'image/jpeg',
          size: fileToSend.size,
          theme_id: themeId,
          child_name: childName,
          age: age,
          language: language,
          turnstile_token: state.turnstileToken
        }))
      }, 15000);
      if (upRes.status === 429) throw await limitErrorFrom(upRes);
      if (!upRes.ok) throw new Error('upload-url-failed');
      var upJson = await upRes.json();
      state.photoKey = upJson.photo_key;
      state.generationId = upJson.generation_id;

      // 2. PUT the file to storage
      var putRes = await fetchWithTimeout(upJson.presigned_put_url, {
        method: 'PUT',
        headers: { 'Content-Type': fileToSend.type || 'image/jpeg' },
        body: fileToSend
      }, 30000);
      if (!putRes.ok) throw new Error('photo-upload-failed');

      // 3. Request the preview (backend maxDuration is 60s; allow a buffer).
      // The upload-url call consumed the first Turnstile token, so fetch a
      // fresh one - tokens are single-use on the server.
      // The loader runs its own stage narration; only the in-panel status
      // (visible again if we fall back to the details step) needs updating.
      setStep('status', { statusText: msg.generating });
      var previewToken = await freshTurnstileToken(15000);
      var prevRes = await fetchWithTimeout(backendUrl + '/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(withCustomer({
          theme_id: themeId,
          photo_key: state.photoKey,
          generation_id: state.generationId,
          child_name: childName,
          age: age,
          language: language,
          turnstile_token: previewToken
        }))
      }, 70000);
      if (prevRes.status === 429) throw await limitErrorFrom(prevRes);
      if (!prevRes.ok) throw new Error('preview-failed');
      var prevJson = await prevRes.json();

      // 4. Resolve preview (immediate, or poll if queued)
      var previewUrl = prevJson.preview_url;
      if (prevJson.generation_id) state.generationId = prevJson.generation_id;
      if (!previewUrl && prevJson.status === 'queued') {
        previewUrl = await pollPreview(state.generationId);
      }
      if (!previewUrl) throw new Error('preview-empty');
      state.previewUrl = previewUrl;

      // The preview exists and belongs to this visitor - the one moment that
      // should count as a CustomizeProduct.
      trackPreviewGenerated();

      // Loader stays up through the redirect - hiding it here would flash the
      // product page for a beat before the preview page loads.
      completePageLoader();
      handoff(childName, age, language, previewUrl);
    } catch (err) {
      resetTurnstile();
      // Out of previews is not an error the user can retry away - show the
      // gate instead of the inline message and leave the button disabled.
      if (err && err.message === 'rate-limited') {
        showLimitGate(err.loginRequired);
        return;
      }
      // Return to the details step and show the message inline. The user can
      // simply tap "Preview my book" again or change the photo - no dead-end
      // error screen, no separate retry button.
      hidePageLoader();
      if (els.generate) els.generate.removeAttribute('disabled');
      setStep('details');
      showFieldError(friendlyError(err));
    }
  }

  async function pollPreview(generationId) {
    var start = Date.now();
    while (Date.now() - start < 45000) {
      await new Promise(function (r) { setTimeout(r, 2000); });
      var res = await fetch(backendUrl + '/preview-status/' + encodeURIComponent(generationId), {
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) continue;
      var json = await res.json();
      if (json.status === 'ready' && json.preview_url) return json.preview_url;
      if (json.status === 'failed') throw new Error('preview-status-failed');
    }
    throw new Error('preview-timeout');
  }

  function friendlyError(err) {
    var code = (err && err.message) || '';
    // Aborted fetch (our timeout) → the "taking longer than usual" message.
    if (err && err.name === 'AbortError') return msg.timeout;
    if (code === 'photo-upload-failed' || code === 'upload-url-failed') return msg.upload;
    if (code === 'preview-failed' || code === 'preview-empty' || code === 'preview-status-failed') return msg.preview;
    if (code === 'preview-timeout') return msg.timeout;
    return msg.generic;
  }

  /* -------------------------------------------------------------- */
  /*  Hand off to the preview page                                 */
  /* -------------------------------------------------------------- */

  function handoff(childName, age, language, previewUrl) {
    var key = 'kb-book-' + (state.generationId || Date.now());
    var payload = {
      theme_id: themeId,
      product_title: productTitle,
      child_name: childName,
      age: age,
      language: language,
      preview_url: previewUrl,
      photo_key: state.photoKey,
      generation_id: state.generationId,
      consent_at: state.consentAt
    };
    try {
      sessionStorage.setItem(key, JSON.stringify(payload));
    } catch (e) { /* sessionStorage may be unavailable; URL still carries the slug */ }

    var slug = handleize(childName + '-' + productTitle);

    // Remember it on this device so the strip can offer it again later. Only
    // the generated cover URL is kept - never the uploaded photo.
    try {
      if (window.KBRecentPreviews) {
        window.KBRecentPreviews.add({
          k: key,
          book: slug,
          // The product handle, which is what a back-link needs. `book` is the
          // display slug that goes in the ?b= param and is not a real handle.
          themeId: themeId,
          bookTitle: productTitle,
          childName: childName,
          age: age,
          coverUrl: previewUrl
        });
      }
    } catch (e) { /* the strip is a convenience; never block the redirect */ }

    window.location.assign((window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'pages/create-your-book?b=' + encodeURIComponent(slug) + '&k=' + encodeURIComponent(key));
  }

  function handleize(str) {
    return (str || '')
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
  }

  /* -------------------------------------------------------------- */
  /*  Reset                                                         */
  /* -------------------------------------------------------------- */

  var resetButtons = root.querySelectorAll('[data-kb-upload-reset]');
  Array.prototype.forEach.call(resetButtons, function (btn) {
    btn.addEventListener('click', function () {
      els.input.value = '';
      state.file = null;
      state.photoKey = null;
      state.previewUrl = null;
      if (state.objectUrl) { URL.revokeObjectURL(state.objectUrl); state.objectUrl = null; }
      clearFieldError();
      clearUploadMsg();
      if (els.generate) els.generate.removeAttribute('disabled');
      setStep('upload');
      if (els.trigger && els.trigger.focus) els.trigger.focus();
    });
  });

  /* -------------------------------------------------------------- */
  /*  Client-side downscale (keeps payload under serverless limits) */
  /* -------------------------------------------------------------- */

  // Goes by pixels, not bytes: a 12 MP phone photo is often only 2-3 MB.
  // Photos already within DOWNSCALE_MAX_PX pass through untouched.
  function maybeDownscale(file) {
    return new Promise(function (resolve) {
      var img = new Image();
      var url = URL.createObjectURL(file);
      img.onload = function () {
        var scale = Math.min(1, DOWNSCALE_MAX_PX / Math.max(img.width, img.height));
        if (scale >= 1) { URL.revokeObjectURL(url); resolve(file); return; }
        var canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(function (blob) {
          URL.revokeObjectURL(url);
          if (!blob) { resolve(file); return; }
          resolve(new File([blob], file.name.replace(/\.(png)$/i, '.jpg'), { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.9);
      };
      img.onerror = function () { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });
  }
})();
